import Phaser from 'phaser';
import { getDialogueById } from '../../data/dialogues';
import { SCENE_KEYS } from '../../data/gameConstants';
import { getScoreRule } from '../../data/scoreRules';
import { NPC } from '../../objects/NPC';
import { Collectible } from '../../objects/Collectible';
import { SaveSystem } from '../../systems/SaveSystem';
import { ZoneCoreSystems } from '../../systems/ZoneCoreSystems';
import { DecisionPanel } from '../../ui/DecisionPanel';
import { ObjectiveHint } from '../../ui/ObjectiveHint';
import { SceneTransition } from '../../utils/SceneTransition';
import { AudioSystem } from '../../systems/AudioSystem';
import { ZONE4_EXIT_CLEAR_BLOCKS, ZONE4_POSITIONS, Zone4Map } from './Zone4Map';
import { ZONE5_POSITIONS } from '../zone5/Zone5Map';
import { InteractionZone } from '../../objects/InteractionZone';

export class Zone4QuestFlow {
  private core: ZoneCoreSystems;
  private map: Zone4Map;
  private guide: NPC;
  private keris: Collectible;
  private hint: ObjectiveHint;
  private decision: DecisionPanel;
  private exitZone?: InteractionZone;
  private signZone?: InteractionZone;
  private forkZone?: InteractionZone;
  private riverZone?: InteractionZone;
  private hillZone?: InteractionZone;
  private routeChosen = false;
  private exitOpen = false;
  private transitioning = false;

  constructor(
    core: ZoneCoreSystems,
    map: Zone4Map,
    guide: NPC,
    keris: Collectible,
    hint: ObjectiveHint,
    decision: DecisionPanel,
  ) {
    this.core = core;
    this.map = map;
    this.guide = guide;
    this.keris = keris;
    this.hint = hint;
    this.decision = decision;
  }

  setExitZone(zone: InteractionZone): void {
    this.exitZone = zone;
    this.syncInteractionZones();
  }

  setInteractionZones(zones: {
    sign: InteractionZone;
    fork: InteractionZone;
    river: InteractionZone;
    hill: InteractionZone;
    exit: InteractionZone;
  }): void {
    this.signZone = zones.sign;
    this.forkZone = zones.fork;
    this.riverZone = zones.river;
    this.hillZone = zones.hill;
    this.exitZone = zones.exit;
    this.syncInteractionZones();
  }

  private syncGuideInteract(): void {
    const readDone = this.core.quest.getStatus('zone4_read_keris3') === 'completed';
    const routeDone = this.core.quest.getStatus('zone4_safe_route') === 'completed';
    const meetDone = this.core.quest.getStatus('zone4_meet_guide') === 'completed';
    this.guide.setInteractEnabled(readDone && !routeDone && !meetDone && !this.routeChosen);
  }

  private syncInteractionZones(): void {
    const meetDone = this.core.quest.getStatus('zone4_meet_guide') === 'completed';
    const signDone = this.core.quest.getStatus('zone4_read_sign') === 'completed';
    const canReadSign = !this.routeChosen && meetDone && !signDone;

    this.signZone?.setEnabled(canReadSign);
    this.forkZone?.setEnabled(meetDone && signDone && !this.routeChosen);
    this.riverZone?.setEnabled(meetDone && signDone && !this.routeChosen);
    this.hillZone?.setEnabled(meetDone && signDone && !this.routeChosen);
    this.exitZone?.setEnabled(this.exitOpen);
    this.syncGuideInteract();
  }

  prepareFrame(): void {
    if (this.decision.isVisible()) {
      this.decision.handleInput(this.core.input);
    }
  }

  start(): void {
    let kerisDone = this.core.quest.getStatus('zone4_find_keris3') === 'completed';
    const readDone = this.core.quest.getStatus('zone4_read_keris3') === 'completed';
    const meetDone = this.core.quest.getStatus('zone4_meet_guide') === 'completed';
    const signDone = this.core.quest.getStatus('zone4_read_sign') === 'completed';
    const routeDone = this.core.quest.getStatus('zone4_safe_route') === 'completed';

    this.keris.setUnlocked(false);
    this.syncGuideInteract();

    if (this.keris.isCollected() && !kerisDone) {
      this.core.quest.completeQuest('zone4_find_keris3');
      kerisDone = true;
    }

    if (routeDone) {
      this.routeChosen = true;
      this.openHillRoute(false);
      this.hint.show(
        'Menuju Titik Kumpul Aman',
        ZONE4_POSITIONS.SAFE_EXIT.x,
        ZONE4_POSITIONS.SAFE_EXIT.y - 60,
      );
    } else if (signDone) {
      this.hint.show('Pilih Jalur Aman', ZONE4_POSITIONS.FORK_INTERACT.x, ZONE4_POSITIONS.FORK_INTERACT.y - 60);
    } else if (meetDone) {
      this.core.quest.activateQuest('zone4_read_sign');
      this.hint.show('Baca Papan Petunjuk', ZONE4_POSITIONS.SIGN_INTERACT.x, ZONE4_POSITIONS.SIGN_INTERACT.y - 60);
    } else if (readDone) {
      this.core.quest.activateQuest('zone4_meet_guide');
      this.hint.show('Temui Petugas Evakuasi', ZONE4_POSITIONS.GUIDE.x, ZONE4_POSITIONS.GUIDE.y - 70);
    } else if (kerisDone) {
      if (this.keris.isCollected()) {
        this.startKerisRead();
      } else {
        this.hint.show('Temukan Keris 3', ZONE4_POSITIONS.KERIS.x, ZONE4_POSITIONS.KERIS.y - 60);
      }
    }

    this.syncInteractionZones();

    if (!kerisDone) {
      this.core.showIntroDialogue('zone4_narrator', () => {
        this.core.quest.activateQuest('zone4_find_keris3');
        this.unlockKeris();
      });
    } else if (!readDone && !this.keris.isCollected()) {
      this.unlockKeris();
    }
  }

  onKerisCollected(): void {
    this.core.quest.completeQuest('zone4_find_keris3');
    this.hint.hide();
    this.startKerisRead();
  }

  onSignInteract(): void {
    if (this.routeChosen) return;

    if (this.core.quest.getStatus('zone4_meet_guide') !== 'completed') {
      this.hint.show('Temui Petugas Evakuasi', ZONE4_POSITIONS.GUIDE.x, ZONE4_POSITIONS.GUIDE.y - 70);
      const needGuide = getDialogueById('zone4_sign_need_guide');
      if (needGuide) {
        this.core.dialogue.show(needGuide);
      }
      return;
    }

    const board = getDialogueById('zone4_question_board');
    const afterBoard = () => {
      if (this.core.quest.getStatus('zone4_read_sign') !== 'completed') {
        this.core.quest.completeQuest('zone4_read_sign');
        this.core.quest.activateQuest('zone4_safe_route');
        this.core.refreshQuestPanel(4);
      }
      this.syncInteractionZones();

      if (!this.routeChosen) {
        this.hint.show('Pilih Jalur Aman', ZONE4_POSITIONS.FORK_INTERACT.x, ZONE4_POSITIONS.FORK_INTERACT.y - 60);
        this.showRouteChoice();
      }
    };

    if (board) {
      this.core.dialogue.show(board, afterBoard);
    } else {
      afterBoard();
    }
  }

  onGuideInteract(): void {
    if (this.core.quest.getStatus('zone4_read_keris3') !== 'completed') return;
    if (this.routeChosen) return;

    if (this.core.quest.getStatus('zone4_meet_guide') === 'completed') {
      if (this.core.quest.getStatus('zone4_read_sign') !== 'completed') {
        const remind = getDialogueById('zone4_npc_guide_remind');
        if (remind) {
          this.core.dialogue.show(remind, () => {
            this.syncInteractionZones();
            this.hint.show('Baca Papan Petunjuk', ZONE4_POSITIONS.SIGN_INTERACT.x, ZONE4_POSITIONS.SIGN_INTERACT.y - 60);
          });
        } else {
          this.syncInteractionZones();
          this.hint.show('Baca Papan Petunjuk', ZONE4_POSITIONS.SIGN_INTERACT.x, ZONE4_POSITIONS.SIGN_INTERACT.y - 60);
        }
        return;
      }

      if (!this.routeChosen) {
        this.hint.show('Pilih Jalur Aman', ZONE4_POSITIONS.FORK_INTERACT.x, ZONE4_POSITIONS.FORK_INTERACT.y - 60);
      }
      return;
    }

    const npc = getDialogueById('zone4_npc_guide');
    const afterNpc = () => {
      this.core.quest.completeQuest('zone4_meet_guide');
      this.core.quest.activateQuest('zone4_read_sign');
      this.core.refreshQuestPanel(4);
      this.syncInteractionZones();
      this.hint.show('Baca Papan Petunjuk', ZONE4_POSITIONS.SIGN_INTERACT.x, ZONE4_POSITIONS.SIGN_INTERACT.y - 60);
    };

    if (npc) {
      this.core.dialogue.show(npc, afterNpc);
    } else {
      afterNpc();
    }
  }

  onForkInteract(): void {
    if (!this.canChooseRoute()) return;
    if (this.routeChosen) return;
    this.showRouteChoice();
  }

  onRiverInteract(): void {
    if (!this.canChooseRoute()) return;
    if (this.routeChosen) return;
    this.handleRouteChoice('river');
  }

  onHillInteract(): void {
    if (this.exitOpen) return;
    this.onForkInteract();
  }

  update(playerX: number, playerY: number): void {
    if (!this.exitOpen || this.transitioning) return;
    if (this.core.dialogue.isActive() || this.decision.isVisible()) return;

    const hill = ZONE4_POSITIONS.HILL_ROUTE;
    const exit = ZONE4_POSITIONS.SAFE_EXIT;
    const hillDist = Phaser.Math.Distance.Between(playerX, playerY, hill.x, hill.y);
    const exitDist = Phaser.Math.Distance.Between(playerX, playerY, exit.x, exit.y);

    if (hillDist < 110) {
      this.hint.show(
        'Menuju Titik Kumpul Aman',
        exit.x,
        exit.y - 60,
      );
    }

    if (exitDist < 100) {
      this.transitionToZone5();
    }
  }

  onExitInteract(): void {
    if (!this.exitOpen || this.transitioning) return;
    this.transitionToZone5();
  }

  private canChooseRoute(): boolean {
    if (this.core.quest.getStatus('zone4_read_keris3') !== 'completed') return false;
    if (this.core.quest.getStatus('zone4_meet_guide') !== 'completed') {
      this.hint.show('Temui Petugas Evakuasi', ZONE4_POSITIONS.GUIDE.x, ZONE4_POSITIONS.GUIDE.y - 70);
      return false;
    }
    if (this.core.quest.getStatus('zone4_read_sign') !== 'completed') {
      this.hint.show('Baca Papan Petunjuk', ZONE4_POSITIONS.SIGN_INTERACT.x, ZONE4_POSITIONS.SIGN_INTERACT.y - 60);
      return false;
    }
    return true;
  }

  private unlockKeris(): void {
    this.keris.setUnlocked(true);
    this.hint.show('Temukan Keris 3', ZONE4_POSITIONS.KERIS.x, ZONE4_POSITIONS.KERIS.y - 60);
  }

  private startKerisRead(): void {
    const kerisDialogue = getDialogueById('zone4_keris3');
    const localDialogue = getDialogueById('zone4_keris3_local');

    const showGuideStage = () => {
      this.core.quest.completeQuest('zone4_read_keris3');
      this.core.quest.activateQuest('zone4_meet_guide');
      this.syncInteractionZones();
      this.hint.show('Temui Petugas Evakuasi', ZONE4_POSITIONS.GUIDE.x, ZONE4_POSITIONS.GUIDE.y - 70);
    };

    const showLocal = () => {
      if (localDialogue) {
        this.core.dialogue.show(localDialogue, showGuideStage);
      } else {
        showGuideStage();
      }
    };

    if (kerisDialogue) {
      this.core.dialogue.show(kerisDialogue, showLocal);
    } else {
      showLocal();
    }
  }

  private showRouteChoice(): void {
    if (this.decision.isVisible() || this.routeChosen) return;
    if (!this.canChooseRoute()) return;

    this.core.setMobileControlsVisible(false);

    const board = getDialogueById('zone4_question_board');
    const question = board?.lines[0]?.text
      ?? 'Jalur mana yang paling tepat dipilih saat terjadi gempa di perbukitan?';

    this.decision.show(
      'Pilih Jalur Evakuasi',
      question,
      [
        { id: 'river', label: 'Jalur tepi sungai' },
        { id: 'hill', label: 'Jalur bukit terbuka' },
      ],
      (id) => {
        this.core.setMobileControlsVisible(true);
        this.handleRouteChoice(id);
      },
    );
  }

  private handleRouteChoice(choice: string): void {
    if (choice === 'river') {
      AudioSystem.playSfx('ERROR');
      const rule = getScoreRule('zone4_wrong_route');
      this.core.score.showScoreFeedback(rule ? rule.points : 0);

      const wrong = getDialogueById('zone4_wrong_route');
      const retry = () => {
        this.hint.show('Pilih Jalur Aman', ZONE4_POSITIONS.FORK_INTERACT.x, ZONE4_POSITIONS.FORK_INTERACT.y - 60);
        this.showRouteChoice();
      };

      if (wrong) {
        this.core.dialogue.show(wrong, retry);
      } else {
        retry();
      }
      return;
    }

    this.routeChosen = true;
    this.hint.hide();
    this.core.quest.completeQuest('zone4_safe_route');
    this.syncInteractionZones();

    const rule = getScoreRule('zone4_safe_route');
    if (rule) this.core.score.addScore(rule.points, rule.label);

    const success = getDialogueById('zone4_safe_route');
    const openHill = () => this.openHillRoute(true);

    if (success) {
      this.core.dialogue.show(success, openHill);
    } else {
      openHill();
    }
  }

  private openHillRoute(showHint = true): void {
    this.exitOpen = true;
    this.map.openExitPath();
    this.core.removeObstaclesAtCenters(ZONE4_EXIT_CLEAR_BLOCKS, 55);
    this.syncInteractionZones();
    this.core.quest.activateQuest('zone4_to_zone5');
    if (showHint) {
      this.hint.show(
        'Menuju Jalur Bukit Terbuka',
        ZONE4_POSITIONS.HILL_ROUTE.x,
        ZONE4_POSITIONS.HILL_ROUTE.y - 60,
      );
    }
  }

  private transitionToZone5(): void {
    if (this.transitioning) return;
    this.transitioning = true;
    this.hint.hide();
    this.core.quest.completeQuest('zone4_to_zone5');
    this.core.quest.activateQuest('zone5_go_safe_zone');

    SaveSystem.save({
      currentScene: SCENE_KEYS.ZONE5,
      currentZone: 5,
      rafflesiaPoints: this.core.score.getScore(),
      playerX: ZONE5_POSITIONS.SPAWN.x,
      playerY: ZONE5_POSITIONS.SPAWN.y,
    });

    SceneTransition.startNextFrame(this.core.scene, SCENE_KEYS.ZONE5);
  }

  destroy(): void {
    this.decision.destroy();
    this.hint.destroy();
  }
}
